// src/component/MM2025Dialog.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  Slide,
  Box,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

// ---- Transition ----
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * 2025年 MasterMind 活動のダイジェストダイアログ
 */
export default function MM2025Dialog({ open, onClose, accent = "#5A35FF" }) {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      scroll="paper"
      PaperProps={{
        sx: {
          maxWidth: "960px",
          width: "100%",
          mx: "auto",
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          boxShadow:
            "0 22px 60px rgba(15,10,40,0.35), 0 0 0 1px rgba(255,255,255,0.8)",
          background:
            "radial-gradient(circle at 0% 0%, #ffffff, #fdf7ff 45%, #f7fbff 100%)",
        },
      }}
    >
      {/* ヘッダー（固定） */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: `${accent}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: accent }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              opacity: 0.65,
            }}
          >
            MASTERMIND 2025
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "0.04em",
            }}
          >
            2025 年の活動の奇跡
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* スクロール領域 */}
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          maxHeight: fullScreen ? "calc(100vh - 64px)" : "70vh",
          overflowY: "auto",
        }}
      >
        {/* イントロ */}
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.9,
            color: "rgba(40,40,60,0.8)",
            mb: 3,
          }}
        >
          2025 年のマスターマインドでは、
          <b>「まんまるマルシェ」</b> と <b>「八重の誓い」</b> の 2
          本柱で一年を紡いでいきました。
          ここでは、その上半期と八重の誓いの歩みを、ざっと振り返れるようまとめています。
        </Typography>

        {/* =========================
            2025 上半期（まんまるマルシェ）
           ========================= */}
        <SectionTitle
          accent={accent}
          label="2025 上半期（〜 7/5）"
          subtitle="夏至に向かって 手足を動かし 夢を叶える"
        />
        {/* https://youtu.be/WUEzBjNlntA?si=pjhI6a7WFC6FaxRN&t=1081 */}
        <Box sx={{ mt: 4, mb: 1 }}>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>
            2025 上半期（〜 2025年7月5日）ダイジェスト（YouTube）
          </Typography>
          <Link
            href="https://youtu.be/WUEzBjNlntA?si=pjhI6a7WFC6FaxRN&t=1081"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 13 }}
          >
            https://youtu.be/WUEzBjNlntA?si=pjhI6a7WFC6FaxRN&amp;t=1081
          </Link>
        </Box>
        <YoutubeEmbed videoId="WUEzBjNlntA" />

        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.9,
            color: "rgba(40,40,60,0.85)",
            mb: 1.5,
          }}
        >
          3/30 と 7/5 の 2
          回にわたり、「まんまるマルシェ」を全国・全世界で同時開催。
          みんなで手足を動かしながら、お互いの夢を叶え合う時間となりました。
        </Typography>


        {/* まんまるマルシェとは */}
        <SubSectionTitle accent={accent}>まんまるマルシェとは…</SubSectionTitle>
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.9,
            color: "rgba(40,40,60,0.85)",
            mb: 2.5,
          }}
        >
          まんまる（真円）――それは完全なる円。
          どこにも角がなく、どこにも負担がかからない正無限角形。
          <br />
          <br />
          まんまるマルシェは、
          <b>「誰もが主役だけど、誰にも負担がない」</b>、
          <b>「誰もリーダーではないけれど、みんなが責任者」</b>
          という在り方でつくられる場です。
          <br />
          それぞれが大好きなことを表現し、大好きな役割を果たし合うことで、
          ひとつの空間が立ち上がっていきます。
          <br />
          <br />
          「とにかく好き！」を爆発させた作品やパフォーマンスで溢れ、
          出店者・来場者・オンライン参加者が、互いの夢を叶え合っていく場所。
          <br />
          それが、<b>まんまるマルシェ</b> です。
        </Typography>

        {/* 上半期 タイムライン１ */}
        <SubSectionTitle accent={accent}>
          【 活動の奇跡 】〜 3/30 まんまるマルシェへ 〜
        </SubSectionTitle>

        <TimelineList
          items={[
            "1/13　DDP設定・深堀会",
            "2・3月　マルシェ情報共有会",
            "2/23　第1回 全体 Zoom 会",
            "3/20　＜春分点 18:01＞ 7.89Hz 純正律音源 配信開始",
            "3/30　まんまるマルシェ　46都道府県55会場＋海外（オンライン・ケニア）",
          ]}
        />

        {/* 上半期 メイン画像＋リンク */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{ mb: 2.5 }}
        >
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 12px 30px rgba(15,10,40,0.25)",
            }}
          >
            {/* ★ 実画像のパスに差し替えてください */}
            <Box
              component="img"
              src="/images/mm2025_overseas_marche_main.jpg"
              alt="海外：24時間世界各国リレーマルシェ"
              sx={{ width: "100%", display: "block" }}
            />
          </Box>

          {/* 右：スピーカー写真 */}
          <Box
            sx={{
              flex: { xs: "unset", sm: 0.8 },
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 12px 30px rgba(15,10,40,0.18)",
            }}
          >
            {/* ★ 実画像のパスに差し替え */}
            <Box
              component="img"
              src="/images/mm2025_overseas_marche_talk.jpg"
              alt="まんまるマルシェ トークシーン"
              sx={{ width: "100%", display: "block", height: "100%" }}
            />
          </Box>
        </Stack>


        {/* https://www.youtube.com/watch?v=-9QP2VYguDo */}
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>
            海外：24時間世界各国リレーマルシェ（YouTube）
          </Typography>
          <Link
            href="https://www.youtube.com/watch?v=-9QP2VYguDo"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 13 }}
          >
            https://www.youtube.com/watch?v=-9QP2VYguDo
          </Link>
        </Box>
        <YoutubeEmbed videoId="-9QP2VYguDo" />



        {/* 上半期 タイムライン２ */}
        <SubSectionTitle accent={accent}>
          〜 7/5 まんまるマルシェ ＆ 神鍋高原フェスへ 〜
        </SubSectionTitle>

        <TimelineList
          items={[
            "4/4　グループ活動（DDP添削活動）夢叶えアドバイス／夢ナビ",
            "5・6月　マルシェ情報共有会",
            "5/25　第2回 全体 Zoom 会",
            "6/29　第3回 全体 Zoom 会",
            "6/21　＜夏至点 11:42＞ 7.92Hz 純正律音源 配信開始",
            "7/5　神鍋フェス（マスターマインドムラ・マスターマインドステージ）＠神鍋高原",
            "　　 まんまるマルシェ　44都道府県47会場 ＋ 海外（オンライン・韓国・ケニア）",
          ]}
        />
        {/* 画像セット２ */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ my: 2.5 }}
        >
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 10px 26px rgba(15,10,40,0.2)",
            }}
          >
            {/* ★ 実画像パスに差し替え */}
            <Box
              component="img"
              src="/images/mm2025_marche_tree_sign.jpg"
              alt="まんまるマルシェ in Kenya"
              sx={{ width: "100%", display: "block" }}
            />
          </Box>
          <Box
            sx={{
              flex: 1.2,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 10px 26px rgba(15,10,40,0.2)",
            }}
          >
            {/* ★ 実画像パスに差し替え */}
            <Box
              component="img"
              src="/images/mm2025_mastermind_stage.png"
              alt="Mastermind Stage"
              sx={{ width: "100%", display: "block" }}
            />
          </Box>
        </Stack>

        {/* =========================
            八重の誓い
           ========================= */}
        <SectionTitle
          accent={accent}
          label="2025 八重の誓い"
          subtitle="冬至に向かって 自分の内面に深く潜っていく"
        />
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>
            八重の誓い ダイジェスト（YouTube）
          </Typography>
          <Link
            href="https://youtu.be/T4xAPdNt95A?si=4dp_wapAz7Ic3swh&t=518"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 13 }}
          >
            https://youtu.be/T4xAPdNt95A?si=4dp_wapAz7Ic3swh&amp;t=518
          </Link>
        </Box>
        <YoutubeEmbed videoId="T4xAPdNt95A" />

        {/* 八重の誓い 画像 */}
        <Box
          sx={{
            mb: 2.5,
            borderRadius: 2,
            overflow: "hidden",
            maxWidth: 520,
            mx: "auto",
            boxShadow: "0 12px 30px rgba(15,10,40,0.25)",
          }}
        >
          {/* ★ 実画像パスに差し替え */}
          <Box
            component="img"
            src="/images/yae_chikai_grid.png"
            alt="八重の誓い 88の問い"
            sx={{ width: "100%", display: "block" }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.9,
            color: "rgba(40,40,60,0.85)",
            mb: 2.5,
          }}
        >
          「八重の誓い」は、<b>88 の問い</b> を通じて、 自分自身の “本当”
          を照らし出していく内省のプログラム。
          <br />
          <br />
          私たちは、いくつもの「自分」を内に抱えて生きています。
          本音と建前、理想と現実、過去と未来…。
          それらが交差する場所にこそ、あなたの本質が眠っています。
          <br />
          <br />
          八重の誓いでは、その奥深くへと一歩ずつ潜っていき、
          やがてそれらを統合して、新たな一歩を踏み出していきます。
        </Typography>


        {/* フェーズごとのタイムライン */}
        <SubSectionTitle accent={accent}>
          【 活動の奇跡 】〜 フェーズ 1「自分を知り、心を開く」〜
        </SubSectionTitle>
        <TimelineList
          items={["8/8　八重の誓い 回答スタート", "9/1　グループ活動"]}
        />

        <SubSectionTitle accent={accent}>
          〜 フェーズ 2「深層を掘り下げ、心の中を探る」〜
        </SubSectionTitle>
        <TimelineList
          items={[
            "9/23　＜秋分点 3:19＞ 7.89Hz 純正律音源 配信開始",
            "9/28　第1回 全体 Zoom 会",
            "10/29　第1回 八重のツドイ",
          ]}
        />

        <SubSectionTitle accent={accent}>
          〜 フェーズ 3「自分を統合し、新たな一歩を踏み出す」〜
        </SubSectionTitle>
        <TimelineList
          items={[
            "11/2　第2回 全体 Zoom 会",
            "11/24　第1回 八重のツドイ",
            "11/30　まんまるマルシェ（都道府県会場＋海外：オンライン・イタリア・ケニア／32都道府県33会場）",
            "12/7　第3回 全体 Zoom 会",
          ]}
        />

        {/* クロージング */}
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.9,
              color: "rgba(40,40,60,0.8)",
            }}
          >
            こうして 2025 年、
            <b>「まんまるマルシェ」</b> で外側の世界を動かし、
            <b>「八重の誓い」</b> で内側の世界を深めていった歩みが、 次の{" "}
            <b>MasterMind Members 2026</b> へとつながっていきます。
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ====== サブコンポーネント ====== */

function SectionTitle({ accent, label, subtitle }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
        <Box
          sx={{
            width: 10,
            height: 28,
            borderRadius: 999,
            background: `linear-gradient(180deg, ${accent}, ${accent}66)`,
          }}
        />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            color: "rgba(40,40,60,0.7)",
            ml: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ★ ここを「全部太く＆大きく」する
function SubSectionTitle({ accent, children }) {
  return (
    <Typography
      sx={{
        mt: 3,
        mb: 1.5,
        fontSize: { xs: 16, sm: 18 },
        fontWeight: 900,
        color: accent,
        letterSpacing: "0.06em",
        lineHeight: 1.5,
        textShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      {children}
    </Typography>
  );
}

function TimelineList({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        pl: 0,
        mb: 2.5,
        fontSize: 13,
        color: "rgba(40,40,60,0.85)",
      }}
    >
      {items.map((line) => (
        <Box
          component="li"
          key={line}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 0.4,
          }}
        >
          <Box
            sx={{
              mt: "7px",
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.2)",
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 13, lineHeight: 1.7 }}>{line}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** YouTube 埋め込み（16:9・レスポンシブ） */
function YoutubeEmbed({ videoId }) {
  if (!videoId) return null;
  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
        position: "relative",
        pt: "56.25%", // 16:9
      }}
    >
      <Box
        component="iframe"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  );
}
